// Pronóstico de pax. Catering → Operación → Pronóstico de pax.
// Sugiere el pax de un día futuro por contrato/servicio usando el histórico de
// efectivos (media móvil por día de semana + tendencia + señal de feriado), y
// permite ajustar y APLICAR la sugerencia al plan del día (pre-llena efectivos).
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, CalendarClock, CheckCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering, SERVICIO_LABEL } from './shared';

interface Sug {
  contrato_id: string; contrato: string; cliente: string; servicio: string;
  sugerido: number; metodo: string; promedio_dia: number | null; n_dia: number;
  promedio_general: number | null; n_muestras: number; ultimo: number | null; ultima_fecha: string | null;
  tendencia_pct: number | null; es_feriado: boolean; feriado: string | null; plan_actual: number | null;
}
const enUnaSemana = (): string => { const d = new Date(); d.setDate(d.getDate() + 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const DOW = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

export function PronosticoCatering() {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(enUnaSemana());
  const [vals, setVals] = useState<Record<string, string>>({});   // contrato|servicio -> cantidad a aplicar

  const { data: sug = [], isFetching } = useQuery<Sug[]>({
    queryKey: ['cat-pax-sugerido', fecha], queryFn: () => catRpc<Sug[]>('cat_pax_sugerido', { _fecha: fecha }),
  });
  // al cargar sugerencias, precargar los inputs con el valor sugerido
  useEffect(() => {
    const v: Record<string, string> = {};
    sug.forEach((s) => { v[`${s.contrato_id}|${s.servicio}`] = String(s.sugerido ?? 0); });
    setVals(v);
  }, [sug]);

  const feriado = useMemo(() => sug.find((s) => s.es_feriado)?.feriado ?? null, [sug]);
  const dow = useMemo(() => { const d = new Date(fecha + 'T00:00:00'); return DOW[d.getDay()]; }, [fecha]);

  const aplicar = useMutation({
    mutationFn: () => catRpc('cat_pax_aplicar', {
      _fecha: fecha,
      _lineas: sug.map((s) => ({ contrato_id: s.contrato_id, servicio: s.servicio, cantidad: parseFloat(vals[`${s.contrato_id}|${s.servicio}`] || '0') || 0 })),
    }),
    onSuccess: (r: any) => { toast.success(`Plan aplicado (${r.aplicadas} servicios)`); invalidateCatering(qc); void qc.invalidateQueries({ queryKey: ['cat-pax-sugerido', fecha] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4" /> Pronóstico de pax</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Fecha a planificar</label>
              <input type="date" className={`${fieldCls} w-44`} value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
            <span className="pb-2 text-xs text-muted-foreground">{dow} · basado en el histórico del mismo día de semana</span>
            {isFetching && <span className="pb-2 text-xs text-muted-foreground">calculando…</span>}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {feriado && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {fecha} es feriado ({feriado}); la asistencia puede variar — revisa las cantidades antes de aplicar.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Contrato</th><th className="py-1 pr-2">Servicio</th>
                <th className="py-1 pr-2 text-right">Sugerido</th><th className="py-1 pr-2">Base</th>
                <th className="py-1 pr-2 text-right">Tendencia</th><th className="py-1 pr-2 text-right">Plan actual</th>
                <th className="py-1 pr-2 text-right">Aplicar</th>
              </tr></thead>
              <tbody>
                {sug.map((s) => {
                  const key = `${s.contrato_id}|${s.servicio}`;
                  return (
                    <tr key={key} className="border-b last:border-0">
                      <td className="py-1.5 pr-2">{s.cliente}<span className="block text-xs text-muted-foreground">{s.contrato}</span></td>
                      <td className="py-1.5 pr-2">{SERVICIO_LABEL[s.servicio] ?? s.servicio}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-medium">{num(s.sugerido, 0)}</td>
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{s.metodo} · n={s.n_dia || s.n_muestras}{s.ultimo != null ? ` · últ. ${num(s.ultimo, 0)}` : ''}</td>
                      <td className="py-1.5 pr-2 text-right">
                        {s.tendencia_pct == null ? <span className="text-xs text-muted-foreground">—</span>
                          : <span className={`inline-flex items-center gap-0.5 text-xs ${s.tendencia_pct > 0 ? 'text-emerald-600' : s.tendencia_pct < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {s.tendencia_pct > 0 ? <TrendingUp className="h-3 w-3" /> : s.tendencia_pct < 0 ? <TrendingDown className="h-3 w-3" /> : null}{s.tendencia_pct}%
                            </span>}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono text-muted-foreground">{s.plan_actual != null ? num(s.plan_actual, 0) : '—'}</td>
                      <td className="py-1.5 pr-2 text-right">
                        <input className={`${fieldCls} w-20 text-right`} type="number" min="0"
                          value={vals[key] ?? ''} onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.value }))} />
                      </td>
                    </tr>
                  );
                })}
                {!sug.length && <tr><td colSpan={7} className="py-3 text-sm text-muted-foreground">No hay histórico suficiente para pronosticar (se necesitan efectivos de fechas anteriores).</td></tr>}
              </tbody>
            </table>
          </div>
          {!!sug.length && (
            <div className="flex items-center gap-3">
              <Button disabled={aplicar.isPending} onClick={() => aplicar.mutate()}>
                <CheckCheck className="mr-1 h-4 w-4" /> Aplicar al plan del {fecha}
              </Button>
              <span className="text-xs text-muted-foreground"><CalendarClock className="mr-1 inline h-3.5 w-3.5" />Pre-llena los efectivos (pax plan). Puedes ajustar cada cantidad antes de aplicar; luego se confirma lo servido en sitio.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
